import "@/lib/i18n";
import { createRootRouteWithContext, Outlet } from "@tanstack/react-router";
import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { type QueryClient } from "@tanstack/react-query";
import { type AuthSlice } from "@njbsoft/store";
import { ErrorBoundary } from "@/components/error-boundary";
import { NotFound } from "@/components/not-found";
import { useIsDesktop } from "@/hooks/use-is-desktop";
import { useThemeSync } from "@/hooks/use-theme-sync";
import { useLocaleSync } from "@/hooks/use-locale-sync";
import { Toaster } from "sonner";

interface RouterContext {
  queryClient: QueryClient;
  auth: AuthSlice;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  notFoundComponent: NotFound,
});

/**
 * The Query and router devtools toggles, from `lg` only. Below `lg` the phone
 * bottom bar fills the bottom edge and a toggle there takes its taps, so neither
 * is mounted at all: their own styles are unlayered and would beat any utility
 * class that tried to hide them.
 */
function DevtoolsFromLg() {
  const desktop = useIsDesktop();
  if (!desktop) return null;
  return (
    <>
      <ReactQueryDevtools buttonPosition="bottom-left" />
      <TanStackRouterDevtools position="bottom-right" />
    </>
  );
}

function RootComponent() {
  useThemeSync();
  useLocaleSync();

  return (
    <>
      <ErrorBoundary>
        <Outlet />
      </ErrorBoundary>
      <Toaster richColors position="top-right" />
      {import.meta.env.DEV && <DevtoolsFromLg />}
    </>
  );
}
